import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { API_URL } from "./constants";
import "./LeaderboardTab.css";

// Phase J. Sibling of LeaderboardTab but for RSVP engagement.
// Two distinct metrics surfaced per user:
//   - RSVPs (= goingCount): events they said they'd attend.
//   - Events (= totalEngagement): any non-Pending vote (Going OR
//     Not Going). The wider engagement signal.
// Pending is the default "didn't engage at all" state and is
// filtered out of both counts. Rows are sorted by RSVPs desc, ties
// broken by total engagement, so the top of the board answers
// "who actually shows up."
//
// Source: socialEvents.rsvps map, snapshotted once when an event
// closes. Until the snapshot writer ships, the leaderboard will be
// empty for every range — that's expected.
//
// Per-row drill-through: tap a user to expand the list of events they
// engaged with in the window, with which option they picked on each.

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const pad2 = (n) => String(n).padStart(2, "0");
const toIso = (year, month0, day) =>
  `${year}-${pad2(month0 + 1)}-${pad2(day)}`;
const daysInMonth = (year, month0) => new Date(year, month0 + 1, 0).getDate();

const computePreset = (id, now = new Date()) => {
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  switch (id) {
    case "mtd":
      return {
        start: toIso(year, month, 1),
        end: toIso(year, month, day),
      };
    case "lastMonth": {
      const lm = new Date(year, month - 1, 1);
      const lmY = lm.getFullYear();
      const lmM = lm.getMonth();
      return {
        start: toIso(lmY, lmM, 1),
        end: toIso(lmY, lmM, daysInMonth(lmY, lmM)),
      };
    }
    case "ytd":
      return {
        start: toIso(year, 0, 1),
        end: toIso(year, month, day),
      };
    case "last90": {
      const from = new Date(now);
      from.setDate(from.getDate() - 89);
      return {
        start: toIso(from.getFullYear(), from.getMonth(), from.getDate()),
        end: toIso(year, month, day),
      };
    }
    case "allTime":
      // Hard floor before the platform existed. Cheaper than asking
      // the backend for a no-range scan and keeps the existing
      // ISO_DATE validation path working. Backend's range clamp is
      // 10 years which is plenty headroom past 2020.
      return {
        start: "2020-01-01",
        end: toIso(year, month, day),
      };
    default:
      return null;
  }
};

const optionBadgeClass = (option) => {
  const o = (option || "").trim().toLowerCase();
  if (o === "going" || o === "yes") return "rsvp-lb__opt rsvp-lb__opt--going";
  if (o === "not going" || o === "no")
    return "rsvp-lb__opt rsvp-lb__opt--not-going";
  return "rsvp-lb__opt";
};

const RsvpLeaderboardTab = () => {
  const { getAuthHeaders } = useAuth();
  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const years = useMemo(
    () => Array.from({ length: 6 }, (_, i) => currentYear - i),
    [currentYear]
  );

  // Default to last 90 days — engagement only matters across a wider
  // window than month-to-date, and the spec called out 90-day default.
  const [preset, setPreset] = useState("last90");
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [range, setRange] = useState(() => computePreset("last90", now));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // userId -> bool. Lets multiple rows be open at once for comparison.
  const [expanded, setExpanded] = useState({});

  // socialEventId -> { open, loading, photos, error }. Lazy-loaded
  // GroupMe photos for individual events. Cached after first fetch
  // so re-toggling doesn't re-hit GroupMe.
  const [photoState, setPhotoState] = useState({});

  const togglePhotos = useCallback(
    async (socialEventId) => {
      const current = photoState[socialEventId];
      // Already open -> collapse (keep cached photos in state for re-open)
      if (current?.open) {
        setPhotoState((prev) => ({
          ...prev,
          [socialEventId]: { ...prev[socialEventId], open: false },
        }));
        return;
      }
      // Have cache, just re-open
      if (current?.photos || current?.error) {
        setPhotoState((prev) => ({
          ...prev,
          [socialEventId]: { ...prev[socialEventId], open: true },
        }));
        return;
      }
      // First open -> fetch
      setPhotoState((prev) => ({
        ...prev,
        [socialEventId]: { open: true, loading: true },
      }));
      try {
        const headers = getAuthHeaders();
        const qs = new URLSearchParams({
          type: "eventGroupMePhotos",
          socialEventId,
        }).toString();
        const resp = await fetch(`${API_URL}?${qs}`, {
          method: "GET",
          headers,
        });
        const body = await resp.json();
        if (!resp.ok) {
          throw new Error(
            body?.error || body?.message || `HTTP ${resp.status}`
          );
        }
        setPhotoState((prev) => ({
          ...prev,
          [socialEventId]: {
            open: true,
            loading: false,
            photos: Array.isArray(body.photos) ? body.photos : [],
            warning: body.warning || null,
          },
        }));
      } catch (err) {
        setPhotoState((prev) => ({
          ...prev,
          [socialEventId]: {
            open: true,
            loading: false,
            error: err.message || "Failed to load photos",
          },
        }));
      }
    },
    [getAuthHeaders, photoState]
  );

  const load = useCallback(
    async (rangeStart, rangeEnd) => {
      setLoading(true);
      setError("");
      try {
        const headers = getAuthHeaders();
        const qs = new URLSearchParams({
          type: "rsvpLeaderboard",
          rangeStart,
          rangeEnd,
        }).toString();
        const resp = await fetch(`${API_URL}?${qs}`, {
          method: "GET",
          headers,
        });
        const body = await resp.json();
        if (!resp.ok) {
          throw new Error(
            body?.error || body?.message || `HTTP ${resp.status}`
          );
        }
        setData(body);
      } catch (err) {
        setError(err.message || "Failed to load RSVP leaderboard");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [getAuthHeaders]
  );

  useEffect(() => {
    load(range.start, range.end);
  }, [range.start, range.end, load]);

  const applyPreset = (id) => {
    const r = computePreset(id, now);
    if (!r) return;
    setPreset(id);
    setRange(r);
    setExpanded({});
  };

  const applyMonthYear = (year, month0) => {
    const isCurrent = year === currentYear && month0 === currentMonth;
    const endDay = isCurrent ? now.getDate() : daysInMonth(year, month0);
    setRange({
      start: toIso(year, month0, 1),
      end: toIso(year, month0, endDay),
    });
    setPreset(null);
    setExpanded({});
  };

  const onYearChange = (e) => {
    const y = parseInt(e.target.value, 10);
    setSelectedYear(y);
    applyMonthYear(y, selectedMonth);
  };

  const onMonthChange = (e) => {
    const m = parseInt(e.target.value, 10);
    setSelectedMonth(m);
    applyMonthYear(selectedYear, m);
  };

  const toggleRow = (userId) =>
    setExpanded((prev) => ({ ...prev, [userId]: !prev[userId] }));

  return (
    <div className="leaderboard-tab rsvp-lb">
      <h2 className="leaderboard-tab__title">RSVP Leaders</h2>
      <p className="leaderboard-tab__info">
        <strong>RSVPs</strong> = events the person said they'd attend
        ("Going"). <strong>Events</strong> = total engagements (Going
        OR Not Going — any active poll vote). Sorted by RSVPs first:
        the headline is "who actually shows up." Compare the two
        columns to spot ghost-voters (high Events, low RSVPs).
      </p>

      <div className="leaderboard-tab__presets">
        {[
          { id: "last90", label: "Last 90 days" },
          { id: "mtd", label: "Month to date" },
          { id: "lastMonth", label: "Last month" },
          { id: "ytd", label: "Year to date" },
          { id: "allTime", label: "All time" },
        ].map((p) => (
          <button
            key={p.id}
            type="button"
            className={
              "leaderboard-tab__preset" +
              (preset === p.id ? " leaderboard-tab__preset--active" : "")
            }
            onClick={() => applyPreset(p.id)}
            disabled={loading}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="leaderboard-tab__controls">
        <label htmlFor="rsvp-lb-month-select">Month:</label>
        <select
          id="rsvp-lb-month-select"
          value={selectedMonth}
          onChange={onMonthChange}
        >
          {MONTHS.map((m, i) => (
            <option key={m} value={i}>
              {m}
            </option>
          ))}
        </select>
        <label htmlFor="rsvp-lb-year-select">Year:</label>
        <select
          id="rsvp-lb-year-select"
          value={selectedYear}
          onChange={onYearChange}
        >
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="leaderboard-tab__btn"
          onClick={() => load(range.start, range.end)}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading && <div className="leaderboard-tab__info">Loading…</div>}
      {error && <div className="leaderboard-tab__error">Error: {error}</div>}

      {data && !loading && (
        <>
          <div className="leaderboard-tab__meta">
            {data.rangeStart} → {data.rangeEnd} · {data.eventsScanned} events
            scanned · {data.eventsWithRsvps} with RSVP snapshots
          </div>

          {data.leaderboard.length === 0 ? (
            <div className="leaderboard-tab__info">
              No RSVP snapshots in this range yet. Until the per-event
              snapshot writer ships, rows here will stay empty — the
              tab is wired so the data starts flowing as soon as
              snapshots get written.
            </div>
          ) : (
            <table className="leaderboard-tab__table rsvp-lb__table">
              <thead>
                <tr>
                  <th className="leaderboard-tab__rank-col">#</th>
                  <th>Member</th>
                  <th className="leaderboard-tab__num-col">RSVPs</th>
                  <th className="leaderboard-tab__num-col">Events</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((row, idx) => {
                  const isOpen = !!expanded[row.userId];
                  return (
                    <React.Fragment key={row.userId}>
                      <tr
                        className="rsvp-lb__row"
                        onClick={() => toggleRow(row.userId)}
                      >
                        <td className="leaderboard-tab__rank-col">{idx + 1}</td>
                        <td>
                          <span className="rsvp-lb__caret">
                            {isOpen ? "▾" : "▸"}
                          </span>{" "}
                          {row.name}
                        </td>
                        <td className="leaderboard-tab__num-col">
                          {row.goingCount}
                        </td>
                        <td className="leaderboard-tab__num-col">
                          {row.totalEngagement}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="rsvp-lb__detail">
                          <td colSpan={4}>
                            <ul className="rsvp-lb__events">
                              {row.events.map((e) => {
                                const ps = photoState[e.socialEventId];
                                return (
                                  <li
                                    key={e.socialEventId}
                                    className="rsvp-lb__event-wrap"
                                  >
                                    <div className="rsvp-lb__event">
                                      <span className="rsvp-lb__event-date">
                                        {e.date ||
                                          e.socialEventId.substring(0, 10)}
                                      </span>
                                      <span className="rsvp-lb__event-title">
                                        {e.title || e.socialEventId}
                                      </span>
                                      <span
                                        className={optionBadgeClass(e.option)}
                                      >
                                        {e.option}
                                      </span>
                                      <button
                                        type="button"
                                        className="rsvp-lb__photo-toggle"
                                        title="Show GroupMe photos for this event"
                                        onClick={() =>
                                          togglePhotos(e.socialEventId)
                                        }
                                      >
                                        {ps?.open ? "📷 Hide" : "📷"}
                                      </button>
                                    </div>
                                    {ps?.open && (
                                      <div className="rsvp-lb__photos-wrap">
                                        {ps.loading && (
                                          <div className="rsvp-lb__photo-status">
                                            Loading photos…
                                          </div>
                                        )}
                                        {ps.error && (
                                          <div className="rsvp-lb__photo-status rsvp-lb__photo-status--error">
                                            {ps.error}
                                          </div>
                                        )}
                                        {!ps.loading &&
                                          !ps.error &&
                                          (!ps.photos ||
                                            ps.photos.length === 0) && (
                                            <div className="rsvp-lb__photo-status">
                                              {ps.warning ||
                                                "No photos in the event chat."}
                                            </div>
                                          )}
                                        {!ps.loading &&
                                          !ps.error &&
                                          ps.photos &&
                                          ps.photos.length > 0 && (
                                            <div className="rsvp-lb__photos">
                                              {ps.photos.map((p) => (
                                                <a
                                                  key={p.messageId + p.url}
                                                  href={p.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="rsvp-lb__photo"
                                                  title={`${p.senderName}`}
                                                >
                                                  <img
                                                    src={p.url}
                                                    alt={p.senderName}
                                                    loading="lazy"
                                                  />
                                                </a>
                                              ))}
                                            </div>
                                          )}
                                      </div>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default RsvpLeaderboardTab;
